import { AppType } from "client/Api/Apps/common";
import {
	FormItem,
	TextInput,
	ValidationError
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import { isNotNil } from "client/Misc/utils";
import { createValidationConfiguration } from "client/Validation";
import { isNotEmpty, isNotNullNorUndefined } from "client/Validation/rules";
import {
	forwardRef,
	ReactElement,
	Ref,
	useCallback,
	useImperativeHandle,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { AppDestinationWindowSelectFormItem } from "../../../AppDestinationWindowSelectFormItem";
import {
	conditionalAccessValidator,
	initialConditionalAccessState,
	prepareConditionalAccessForSaving
} from "../ConditionalAccess";
import { EditAppHandle } from "../EditApp";
import {
	EditAppBase,
	EditAppSettings,
	initialGenericState
} from "../EditAppBase";
import {
	initialMaintenanceWindowState,
	maintenanceWindowValidator,
	prepareMaintenanceWindowForSaving
} from "../MaintenanceWindow";
import { useSaveApp, EditAppSavedResult } from "../useSaveApp";
import {
	EditShortcutAppViewModel,
	SaveShortcutAppModel,
	saveShortcutApp
} from "client/Api/Apps/EditApp/saveApp";

export type EditShortcutAppProps = {
	initialSettings: EditAppSettings<EditShortcutAppViewModel>;
	initiallyActivateWhoHasAccessTab: boolean;
};

export const EditShortcutApp = forwardRef(
	(props: EditShortcutAppProps, ref: Ref<EditAppHandle>): ReactElement => {
		const {
			initialSettings,
			initiallyActivateWhoHasAccessTab
		} = props;

		const [formData, setFormData] = useState<SaveShortcutAppModel>(
			() => {
				const {
					isConnectedToMainApp,
					uri,
					destinationWindow,
					maintenanceWindow,
					conditionalAccessSettings
				} = initialSettings.model
					.app;

				return {
					...initialGenericState(initialSettings),
					uri,
					destinationWindow,
					maintenanceSettings: isConnectedToMainApp
						? null
						: initialMaintenanceWindowState(maintenanceWindow),
					conditionalAccessSettings: isConnectedToMainApp
						? null
						: initialConditionalAccessState(conditionalAccessSettings)
				};
			}
		);

		const {
			app,
			usersAndUserGroupsData
		} = initialSettings.model;

		const {
			canBeConfigured
		} = app;

		const prepareModelForSaving = useCallback(
			(model: SaveShortcutAppModel): SaveShortcutAppModel => {
				return {
					...model,
					uri: canBeConfigured
						? model.uri
						: null,
					maintenanceSettings: prepareMaintenanceWindowForSaving(model.maintenanceSettings),
					conditionalAccessSettings: prepareConditionalAccessForSaving(model.conditionalAccessSettings)
				};
			},
			[canBeConfigured]
		);

		const formHandler: IFormHandler<SaveShortcutAppModel> = useFormHandler(
			formData,
			setFormData,
			canBeConfigured
				? configurableValidation
				: noneConfigurableValidation
		);

		const save: () => Promise<EditAppSavedResult> = useSaveApp<SaveShortcutAppModel>(
			formHandler,
			saveShortcutApp,
			AppType.Url,
			prepareModelForSaving
		);

		useImperativeHandle(
			ref,
			(): EditAppHandle => ({
				save
			}),
			[save]
		);

		const hasValidationErrorForSpecificSettings: boolean | undefined = formHandler
			.under("uri", "destinationWindow")
			.attachToTab()
			.hasValidationError;

		return (
			<EditAppBase
				formHandler={formHandler}
				hasValidationErrorForSpecificSettings={hasValidationErrorForSpecificSettings}
				initialAppSettings={app}
				usersAndUserGroupsData={usersAndUserGroupsData}
				initiallyActivateWhoHasAccessTab={initiallyActivateWhoHasAccessTab}
			>
				<>
					{canBeConfigured && (
						<FormItem
							label={localize("_DefaultWebPage")}
						>
							<TextInput
								{...formHandler.for("uri")
									.attachToTextInput()
								}
								placeholder="http://mywebpage.com"
							/>
							<ValidationError
								{...formHandler
									.for("uri")
									.attachToValidationError()
								}
							/>
						</FormItem>
					)}

					<AppDestinationWindowSelectFormItem
						attachable={formHandler.for("destinationWindow")}
					/>
				</>
			</EditAppBase>
		);
	}
);
EditShortcutApp.displayName = "EditShortcutApp";

const noneConfigurableValidation = createValidationConfiguration<SaveShortcutAppModel>();

noneConfigurableValidation.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

noneConfigurableValidation.forObject("conditionalAccessSettings")
	.whenType(isNotNil, conditionalAccessValidator());

noneConfigurableValidation.forObject("maintenanceSettings")
	.whenType(isNotNil, maintenanceWindowValidator());

const configurableValidation = noneConfigurableValidation.clone();

configurableValidation.forScalar("uri")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);